import { Fragment } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Edit } from '@mint-lib/icons';
import {
  Button,
  Card,
  Divider,
  TruncateText,
  typographyVariants,
} from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

import { useLocalizedGranularityLevelLabels } from '../../constants/i18n/granularityLevel.js';
import { useDataFileContext } from '../../contexts/DataFileContext/useDataFileContext.js';
import { usePlatformsCatalog } from '../../queries/resources.js';

type StaticContextDataV2Props = {
  isEditable: boolean;
  switchToEditingMode: () => void;
};

export const StaticContextDataV2 = ({
  isEditable,
  switchToEditingMode,
}: StaticContextDataV2Props) => {
  const { t } = useTranslation('@myn/import-hub');

  const {
    dataFilePreview: {
      currentStep,
      context: { mediaPlanName, activityName, platformName },
      granularityLevel,
    },
  } = useDataFileContext<'V2'>();

  const isGranularityLevelShown = ['validation', 'channel_assignment'].includes(
    currentStep,
  );
  const granularityLevelLabelByType = useLocalizedGranularityLevelLabels();

  const granularityLevelLabel = granularityLevelLabelByType[granularityLevel];

  const {
    data: { platformsByName },
  } = usePlatformsCatalog();
  const platform = platformsByName[platformName as string];

  const contextItems = [
    // TODO: Refactor to use <Trans> component
    // eslint-disable-next-line @mint/no-t-concatenation
    { label: t('Media plan') + ': ', value: mediaPlanName },
    // TODO: Refactor to use <Trans> component
    // eslint-disable-next-line @mint/no-t-concatenation
    { label: t('Activity') + ': ', value: activityName },
    // TODO: Refactor to use <Trans> component
    // eslint-disable-next-line @mint/no-t-concatenation
    { label: t('Platform') + ': ', value: platform?.label },
    {
      // TODO: Refactor to use <Trans> component
      // eslint-disable-next-line @mint/no-t-concatenation
      label: t('Data attribution level') + ': ',
      value: isGranularityLevelShown ? granularityLevelLabel : null,
    },
  ].filter((item) => Boolean(item.value));

  return (
    <Card className="mb-3" contentClassName="flex w-fit gap-4">
      {contextItems.map((item, index) => (
        <Fragment key={item.label}>
          <div className="flex max-w-[240px] flex-col gap-1">
            <span
              className={typographyVariants({
                variant: 'label01',
                color: 'secondary',
              })}
            >
              {item.label}
            </span>
            <TruncateText>
              <span
                className={cn(
                  typographyVariants({ variant: 'h6', color: 'primary' }),
                  'block',
                )}
              >
                {item.value}
              </span>
            </TruncateText>
          </div>

          {index < contextItems.length - 1 && (
            <Divider orientation="vertical" className="h-auto!" />
          )}
        </Fragment>
      ))}
      {isEditable && (
        <Button
          onClick={switchToEditingMode}
          aria-label={t('Edit context fields')}
          data-dd-action-name="Edit context fields"
          icon
          variant="plain"
          size="large"
          className="ml-4"
        >
          <Edit />
        </Button>
      )}
    </Card>
  );
};
